import { Box, Divider, Grid, IconButton, Paper, Stack, Typography } from '@mui/material'
import EmptyBox from '../../assets/icons/EmptyBox'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import TradePanelReadyMadeStrategies from './tradePanelReadyMadeStrategies'
import { useDispatch, useSelector } from 'react-redux'
import commonActions from '../../actions/common.actions'
import PlChart from '../../pages/home/plChart'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { WidgetListItems } from '../../util/sidebar'
import Trade from '../../assets/icons/Trade'
import { selectBasketId, selectStrategyBuilderMode, selectStrategyBuilderTab, selectTradeOrders } from '../../selectors/strategyBuilder'
import { OptionChainViewKey, ViewType } from '../../assets/constants'
import { StrategyBuilderTab } from '../strategyBuilder/header'
import useSelectPrimaryOrUnderlyingSecurity from '../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import { generatePath, matchPath, useLocation, useNavigate } from 'react-router-dom'
import ROUTES from '../../assets/routes'
import TradePanel from './tradePanel'
import AnalyzePositionsTable from '../analyzePositionsTable/analyzePositionsTable'
import { Exchange, exchangeToJSON } from '../../proto/generated/common'
import { calculateChange, toLocaleStringIn } from '../../util/general'
import { KeyboardArrowLeftRounded } from '@mui/icons-material'
import { selectPositions } from '../../selectors/positions'
import { resetStrategyBuilder } from '../../reducer/strategyBuilder.reducer'
import { fetchSecuritySnapshotsBulk } from '../../services/apis/candles'
import { Security } from '../../proto/generated/candle-data-depot-common'
import { getOrderBooksBulk } from '../../util/tradepanel'
import { updateOrderBooks } from '../../reducer/security.reducer'
import websocketActions from '../../actions/websocket.actions'
import store from '../../store'
import useSelectView from '../../hooks/useSelectView'

const TradePanelContainerV2 = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const tradeOrders = useSelector(selectTradeOrders);
    const basketId = useSelector(selectBasketId);
    const strategyBuilderMode = useSelector(selectStrategyBuilderMode);
    const positions = useSelector(selectPositions);
    const [profitData , setProfitData] = useState<{realisedProfit: number, unrealisedProfit: number}>({
        realisedProfit: 0,
        unrealisedProfit: 0
    })
    const { primaryOrUnderlying: mainSecurity, primaryOrUnderlyingToken: underlyingToken } = useSelectPrimaryOrUnderlyingSecurity()
    const strategySelectedTab = useSelector(selectStrategyBuilderTab)
    const view = useSelectView()
    const showPlChart = useMemo(() => strategySelectedTab !== StrategyBuilderTab.Payoff, [strategySelectedTab])
    const viewKey = useMemo(() => {
        if (matchPath(ROUTES.simulator, location.pathname)) {
            return OptionChainViewKey.HISTORICAL
        }
        return OptionChainViewKey.LIVE

    }, [location.pathname])

    const showReadyMadeStrategies = useMemo(() => {
        if (view === ViewType.OPTION_CHAIN) return true
        if (view === ViewType.STRATEGY_BUILDER && strategySelectedTab === StrategyBuilderTab.OptionChain) return true
        return false
    }, [view, strategySelectedTab])

    const basketPositions = useMemo(() => positions?.filter(p => p.basketId === basketId), [positions, basketId])

    const snapshotRequestInProgress = useRef<string[]>([]);
    const orderBookRequestInProgress = useRef<string[]>([]);

    useEffect(() => {
        let orderBookSecurities: string[] = [];
        let snapshotSecurities: string[] = [];
        let securitiesToSubscribe: string[] = [];
        let exchange: Exchange = Exchange.EXCHANGE_UNKNOWN;

        const { snapshots: snapshotsMap, orderBooks: orderBooksMap } = store.getState().securityStore

        tradeOrders.forEach((t) => {
            exchange = t.security.exchange;
            securitiesToSubscribe.push(t.security.code);
            const key = getSecurityKey(t.security.exchange, t.security.code)
            if (!snapshotSecurities.includes(t.security.code) && !snapshotsMap[key]) {
                snapshotSecurities.push(t.security.code)
            }
            if (!orderBookSecurities.includes(t.security.code) && !orderBooksMap[key]) {
                orderBookSecurities.push(t.security.code)
            }
        })

        basketPositions.forEach((p) => {
            securitiesToSubscribe.push(p.security.code);
            const key = getSecurityKey(p.security.exchange, p.security.code)
            if (!orderBookSecurities.includes(p.security.code) && !orderBooksMap[key]) {
                orderBookSecurities.push(p.security.code)
            }
            if (!snapshotSecurities.includes(p.security.code) && !snapshotsMap[key]) {
                snapshotSecurities.push(p.security.code)
            }
        })

        if (exchange === Exchange.EXCHANGE_UNKNOWN) return;

        orderBookSecurities = orderBookSecurities.filter(code => !orderBookRequestInProgress.current.includes(code));
        snapshotSecurities = snapshotSecurities.filter(code => !snapshotRequestInProgress.current.includes(code));

        snapshotRequestInProgress.current.push(...snapshotSecurities);
        orderBookRequestInProgress.current.push(...orderBookSecurities);

        const fetchSnapshotSecurities: Security[] = snapshotSecurities.map(v => ({ exchange, code: v }))
        const fetchOrderBookSecurities: Security[] = orderBookSecurities.map(v => ({ exchange, code: v }))

        if (fetchSnapshotSecurities.length) {
            fetchSecuritySnapshotsBulk({ securities: fetchSnapshotSecurities, dispatch }).then(() => {
                snapshotRequestInProgress.current = snapshotRequestInProgress.current.filter(code => !snapshotSecurities.includes(code));
            });
        }
        if (fetchOrderBookSecurities.length) {
            getOrderBooksBulk(fetchOrderBookSecurities).then((orderBooks) => {
                dispatch(updateOrderBooks(orderBooks));
                orderBookRequestInProgress.current = orderBookRequestInProgress.current.filter(code => !orderBookSecurities.includes(code));
            });
        }

        const finalSubscribeSecurities = securitiesToSubscribe.map(code => ({ exchange, code }))
        
        websocketActions.setWebsocketSubscribeList(dispatch, {
            orderBookSubscriptions: [
                {
                    tag: 'tradepanel-order-books',
                    securities: finalSubscribeSecurities,
                    topBidAskOnly: true,
                }
            ],
            securitySnapshotSubscriptions: [
                {
                    tag: 'tradepanel-snapshots',
                    securities: finalSubscribeSecurities
                }
            ]
        })
    }, [tradeOrders, basketPositions])

    useEffect(() => {
        return () => {
            websocketActions.setWebsocketSubscribeList(dispatch, {
                orderBookSubscriptions: [
                    {
                        tag: 'tradepanel-order-books',
                        securities: [],
                        topBidAskOnly: true,
                    }
                ],
                securitySnapshotSubscriptions: [
                    {
                        tag: 'tradepanel-snapshots',
                        securities: []
                    }
                ]
            });
        }
        // return () => {
        //     dispatch(resetStrategyBuilder())
        // }
    }, [])

    const [tradePanelSecurity, setTradePanelSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>> | null>(null)
    useEffect(() => {
        const basketPositions = positions.filter(p => p.basketId === basketId);
        if (!basketPositions.length) {
            setTradePanelSecurity(null)
            return
        }
        const positionSecurity = basketPositions[0].security;
        const secKey = getSecurityKey(positionSecurity.exchange, positionSecurity.code);
        getSecurityAsync(secKey).then((sec) => {
            if (!sec?.underlyingSecurityCode) {
                setTradePanelSecurity(null)
                return
            }
            const underlyingKey = getSecurityKey(sec.exchange, sec.underlyingSecurityCode);
            return getSecurityAsync(underlyingKey).then(setTradePanelSecurity)
        }).catch(() => setTradePanelSecurity(null))
    }, [positions, basketId]);

    useEffect(() => {
        tradePanelSecurity && navigate(generatePath(ROUTES.strategyBuilder, { exchange: exchangeToJSON(tradePanelSecurity.exchange), symbol: tradePanelSecurity.code}))
    }, [tradePanelSecurity])

    useEffect(() => {
        if (!underlyingToken.length) {
            // commonActions.dispatchRightSideWidget(dispatch, null)
        }
    }, [underlyingToken])

    const goToPositionsHandler = useCallback(() => {
        dispatch(resetStrategyBuilder());
        commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.POSITIONS)
    }, [])

    const emptyBoxContainer = useMemo(() => {
        if (showReadyMadeStrategies) {
            return <TradePanelReadyMadeStrategies />
        }
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <EmptyBox />
                    <Typography style={{
                        fontSize: '18px',
                        textAlign: 'center',
                        maxWidth: '320px',
                        marginTop: '20px'
                    }}>
                        No security added to trade.
                    </Typography>
                </Box>
            </Box>
        )
    }, [showReadyMadeStrategies])

    const topBarContainer = useMemo(() => {
        return  strategyBuilderMode === 'trade' ? (
            <Grid container px={'8px'} py={'2.5px'} justifyContent={'space-between'} alignItems={'center'}>
                <Stack direction={'row'} gap={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Trade sx={{ color: (theme) => theme.palette.text.secondary }} fontSize={"large"} />
                        <Typography variant={'body1'}>Trade</Typography>
                    </Box>
                </Stack>
            </Grid>
        ): (
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingX: 1,
                paddingY: 0.25,
                width: '100%'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton size="small"
                        onClick={goToPositionsHandler}><KeyboardArrowLeftRounded /></IconButton>
                    <Typography fontSize={'16px'} variant="button"
                        color={'inherit'}>{mainSecurity?.symbol}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {positions.length ? (
                        <Typography className="font-feature-num" variant="caption" sx={{ color: (theme) => theme.palette.text.secondary, lineHeight: (theme) => theme.typography.body1.lineHeight }}>
                            {(() => {
                                return (
                                    <>
                                        REALISED:{" "}
                                        <span
                                            className={
                                                profitData.realisedProfit === 0
                                                    ? undefined
                                                    : profitData.realisedProfit > 0
                                                    ? "green-text"
                                                    : "red-text"
                                            }
                                        >
                                            {toLocaleStringIn(profitData.realisedProfit)}
                                        </span>
                                        {" | TOTAL  : "}
                                        <span
                                            className={
                                                (profitData.realisedProfit + profitData.unrealisedProfit) === 0
                                                    ? undefined
                                                    : (profitData.realisedProfit + profitData.unrealisedProfit) > 0
                                                    ? "green-text"
                                                    : "red-text"
                                            }
                                        >
                                            {toLocaleStringIn(profitData.realisedProfit + profitData.unrealisedProfit)}
                                        </span>
                                    </>
                                )
                            })()}
                        </Typography>
                    ) : null}
                </Box>
            </Box>
        )
    }, [strategyBuilderMode, profitData, mainSecurity, goToPositionsHandler])

    const plChart = useMemo(() => {
        return showPlChart ? (
            <div className="chart-container-wrap"
                style={{ height: '180px', width: '100%' }}>
                <PlChart readOnly={true} optionChainViewKey={viewKey} />
            </div>
        ) : null
    }, [showPlChart, viewKey])

    const divider = useMemo(() => <Divider style={{ width: '100%' }} />, [])

    const handleProfitChange = useCallback((realisedProfit: number, unrealisedProfit: number, ) => {
        setProfitData({realisedProfit, unrealisedProfit})
    }, [])

    const body = useMemo(() => (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {
                strategyBuilderMode === 'trade' && !tradeOrders.length && !basketPositions?.length ?
                    emptyBoxContainer :
                    <>
                        {basketPositions?.length ? <AnalyzePositionsTable handleProfitChange={handleProfitChange} positions={basketPositions} /> : null}
                        <TradePanel />
                    </>
            }
        </Box>
    ), [tradeOrders.length, plChart, emptyBoxContainer, strategyBuilderMode, basketPositions])

    const jsxTree = useMemo(() => {
        return (<Paper
            sx={{
                display: 'flex',
                height: '100%',
                flexDirection: 'column',
                borderTopRightRadius: '6px',
                overflow: 'auto',
                position: 'relative'
            }}
        >
            <Grid container>
                {topBarContainer}
                {divider}
            </Grid>
            {body}
        </Paper>)
    }, [topBarContainer, divider, body])

    return (jsxTree)
}

export default memo(TradePanelContainerV2)